import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '@app/finxone-web-frontend/app/lib/services/multipart-holder/multipart-form-holder.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { paymentReferenceMaxLengths } from '@app/finxone-web-frontend/app/lib/utils/payments/payment-reference.json';
import { customValidator } from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import {
  AccountInfoType,
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { ValidationConfig } from '@finxone-platform/ui-components';
import { Select, Store } from '@ngxs/store';
import { Observable, map, takeUntil } from 'rxjs';
import { CustomValidators } from '../../../../../../../../libs/shared/enums/custom-validators.enum';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';

const paymentRefMaxLengthValidator = (maxLength: number): ValidatorFn => {
  if (!maxLength) return () => null;
  return (control: AbstractControl): { [key: string]: any } | null => {
    return control.value && control.value.length <= maxLength ? null : { maxlength: true };
  };
};

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-payment-ref-widget',
  templateUrl: './payment-ref-widget.component.html',
})
export class PaymentRefWidgetComponent extends FormSubmitter implements OnInit, AfterViewInit {
  @ViewChild('inputFieldContent', { static: false })
  inputFieldContent: ElementRef;
  public inputFieldForm: FormGroup;

  public inputPlaceholder: string;
  public inputStyle: baseCssConfigStyle;
  private nextPageUrl: string;
  public inputFieldKey: string;
  public inputValue = '';
  public colorStyle: baseCssConfigStyle;
  public displayName: string;
  @Select(AccountState.getAccounts)
  private accountStateData!: Observable<AccountStateModel>;
  private selectedAccount: AccountInfoType | undefined;
  public maxLength: number;
  public inputHasKeyDown = false;
  public marginPaddingStyle: baseCssConfigStyle;
  public set setInputHasKeyDown(value: boolean) {
    setTimeout(() => {
      // slight delay to let the formSubmissionService validation catch up
      this.inputHasKeyDown = value;
    }, 100);
  }

  inputLabel: string;

  constructor(
    public renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    public override store: Store,
    private configService: ConfigService,
    private formSubmissionService: FormSubmissionService,
    private formHolderService: MultiPartFormHolderService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
  ) {
    super(store);

    this.accountStateData
      .pipe(
        takeUntil(this.destroy$),
        map((accountData: AccountStateModel) => {
          this.selectedAccount = accountData.accounts.find(
            (account) => account.accountId === accountData.currentAccountId,
          );
          this.onSetPaymentReferenceCustomValidation();
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      if (this.attri.widgetProperties?.inputLabel) {
        this.inputLabel = this.attri.widgetProperties?.inputLabel;
      }

      this.inputPlaceholder = this.attri.widgetProperties['inputPlaceholder'];
      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.MARGIN,
        BaseCssProperties.PADDING,
        BaseCssProperties.BOX_SHADOW,
      ]);

      this.marginPaddingStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.MARGIN,
        BaseCssProperties.PADDING,
      ]);

      this.inputStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.BORDER,
        BaseCssProperties.BACKGROUND_COLOR,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.V_ALIGN,
      ]);

      this.colorStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);

      this.inputFieldKey = this.attri.widgetProperties['inputFieldKey'];
      this.displayName = this.attri.widgetProperties['displayName'] || 'Field';
      this.buildForm();
    }
  }

  ngAfterViewInit(): void {
    this.onSetDynamicStyle();
  }

  public get remainingChars(): string {
    const currentLength = this.inputFieldForm.get(this.inputFieldKey)?.value?.length ?? 0;
    const remaining = this.maxLength - currentLength;
    return `<div class="mx-1 typog-xs"> ${remaining} characters remaining.</div>`;
  }

  public get validationConfig(): ValidationConfig[] {
    return [
      {
        type: 'required',
        message: `${this.displayName} is a required field`,
      },
      {
        type: 'maxlength',
        params: this.maxLength,
        message: `${this.displayName} exceeds max amount of characters`,
      },
      {
        type: 'pattern',
        params: this.attri.widgetProperties['pattern'],
        message: `${this.displayName} contains invalid characters`,
      },
    ];
  }

  private buildForm() {
    this.onSetPaymentReferenceCustomValidation();

    if (this.attri.widgetProperties['maxLength']) {
      this.inputFieldForm
        .get([this.inputFieldKey])
        ?.addValidators(Validators.maxLength(this.attri.widgetProperties['maxLength']));
      this.maxLength = this.attri.widgetProperties['maxLength'];
    }

    if (this.attri.widgetProperties['pattern']) {
      this.inputFieldForm
        .get([this.inputFieldKey])
        ?.addValidators(Validators.pattern(this.attri.widgetProperties['pattern']));
    }

    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.PAYMENT_REF_WIDGET,
      route: '/payments/initiate-a-payment-account-selector',
      form: this.inputFieldForm,
    });

    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);

    const stage = this.formHolderService.config.getStage(WidgetNames.PAYMENT_REF_WIDGET);
    this.inputFieldForm = stage.getForm();

    this.subscribeToFormAction().subscribe((response) => {
      if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
      if (response?.type === 'submitStep') this.onSubmit();
      if (response?.formData?.[this.inputFieldKey]) this.patchNameFormValues(response.formData);
    });
  }

  private onCreateForm() {
    this.inputFieldForm = new FormGroup({
      [this.inputFieldKey ?? this.dataIn?.attri?.widgetProperties?.['inputFieldKey']]: new FormControl('', [
        Validators.required,
      ]),
    });
  }

  private onSetPaymentReferenceCustomValidation() {
    // Only create the form once - this also runs from the account-state subscription in the
    // constructor, which re-fires whenever account data changes later (e.g. a balance refresh
    // or another widget switching the selected account). Recreating the form on every such
    // emission discarded whatever value had already been patched into it from stored state.
    if (!this.inputFieldForm) {
      this.onCreateForm();
    }
    if (this.dataIn?.attri?.widgetProperties?.['customValidator'] && this.selectedAccount?.currency) {
      this.maxLength = this.getMaxLengthFromJson(this.dataIn?.attri?.widgetProperties?.['customValidator']);
      this.inputFieldForm
        .get([this.inputFieldKey ?? this.dataIn?.attri?.widgetProperties?.['inputFieldKey']])
        ?.addValidators(customValidator(paymentRefMaxLengthValidator(this.maxLength)));
    }
  }

  private getMaxLengthFromJson(
    type: CustomValidators.PAYMENT_REF_MAX_LENGTH_LOCAL | CustomValidators.PAYMENT_REF_MAX_LENGTH_SWIFT,
  ): number {
    if (type == CustomValidators.PAYMENT_REF_MAX_LENGTH_SWIFT) {
      return paymentReferenceMaxLengths[this.selectedAccount?.currency as string]?.['swift'].maxLength ?? 132;
    } else {
      return paymentReferenceMaxLengths[this.selectedAccount?.currency as string]?.['local'].maxLength ?? 18;
    }
  }

  private truncateInputValueUsingMaxLength(rawValue: string | null | undefined): void {
    const value = (rawValue ?? '')?.slice(0, this.maxLength);
    this.inputValue = value;
    this.inputFieldForm.get(this.inputFieldKey)?.setValue(value);
  }

  public patchNameFormValues(formData: Record<string, string>): void {
    this.truncateInputValueUsingMaxLength(formData?.[this.inputFieldKey]);

    const formIndex = this.formSubmissionService.currentPageFormGroups.findIndex((item) =>
      item.get(this.inputFieldKey),
    );

    if (formIndex > -1) {
      this.formSubmissionService.updateCurrentPageFormGroups(formIndex, this.inputFieldForm);
    }
  }

  public onSubmit(): void {
    this.truncateInputValueUsingMaxLength(this.inputFieldForm.value?.[this.inputFieldKey]);

    if (this.inputFieldForm.valid) {
      this.formSubmissionService.submitIndividualForm(
        this.inputFieldForm.value as Record<string, string>,
        this.nextPageUrl,
      );
    }
  }

  public onChangeConfig(): void {
    this.onSubmit();
  }

  public onSetDynamicStyle(): void {
    if (this.dataIn?.attri && this.inputFieldContent?.nativeElement) {
      this.renderer.setStyle(
        this.inputFieldContent.nativeElement,
        '--input-field-widget-placeholder-color',
        this.dataIn.attri.css_config?.['inputPlaceholderColor'],
        2,
      );

      this.renderer.setStyle(
        this.inputFieldContent.nativeElement,
        '--input-field-widget-font-size',
        `${this.dataIn.attri.css_config?.fontSize}px`,
        2,
      );
      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }
}
